import { useState, useEffect } from 'react';
import {X, Sparkles, Phone, Mail, Loader2, ArrowRight} from 'lucide-react';
import toast from 'react-hot-toast';
import { useFormDraft } from '@/hooks/useFormDraft';

// Clé de brouillon local pour ne jamais perdre les données saisies.
const AVAILABILITY_FORM_DRAFT_KEY = 'bnssa-mns:draft:availability-form';
import {
  FRENCH_REGIONS,
  FRENCH_DEPARTMENTS,
  MOBILITY_RADIUS_OPTIONS,
  CANDIDATE_QUALIFICATIONS,
  CANDIDATE_SPECIALTIES,
  SPECIALTY_LABELS,
  WORK_ENVIRONMENTS,
  EXPERIENCE_LEVELS,
  formatPeriodRange,
  type CandidateQualification,
  type CandidateSpecialty,
  type CandidateWorkEnvironment,
  type CandidateExperience,
  type CandidateMobility,
  type CandidateHousingNeed,
  type MobilityRadiusKm,
  type AvailabilityMode,
  type AvailableCandidateInput,
} from '@/hooks/useAvailableCandidates';

interface Props {
  /** Email pré-rempli (si un visiteur a déjà saisi quelque chose, sinon vide). */
  email?: string;
  /**
   * Brouillon de repli (ex. brouillon de pré-inscription déjà soumis) utilisé
   * pour pré-remplir les champs si le brouillon local `useFormDraft` est vide.
   * Permet de retrouver ses données en rouvrant la pop-up après être allé sur
   * /inscription sans terminer la création de compte.
   */
  fallbackDraft?: Record<string, unknown> | null;
  onClose: () => void;
  onSave: (data: AvailableCandidateInput) => void | Promise<void>;
  /** Libellé du bouton de soumission (« Continuer » pour un visiteur, « Publier » si connecté). */
  submitLabel?: string;
  saving?: boolean;
}

/**
 * AvailabilityFormModal — Pop-up de saisie des disponibilités candidat.
 *
 * Reprend les mêmes champs que l'onglet « Mes disponibilités » du dashboard
 * (mode immédiat 48h / période datée, qualification, ville, région, téléphone,
 * email, mobilité, logement), mais SANS dépendance à un compte connecté : la
 * pop-up se contente de collecter les données et de les transmettre via
 * `onSave`. Le téléversement de diplôme reste réservé au dashboard.
 */
export default function AvailabilityFormModal({
  email,
  fallbackDraft,
  onClose,
  onSave,
  submitLabel = 'Continuer',
  saving = false,
}: Props) {
  // Brouillon local : restauré au montage, sauvegardé à chaque modification.
  const { initialDraft, saveDraft, clearDraft } = useFormDraft<Record<string, unknown>>(
    AVAILABILITY_FORM_DRAFT_KEY
  );
  // Le brouillon local prime ; sinon on retombe sur le brouillon de pré-inscription.
  const d = (initialDraft || fallbackDraft || {}) as Record<string, unknown>;

  const [qualification, setQualification] = useState<CandidateQualification>(
    (d.qualification as CandidateQualification) || 'BNSSA'
  );
  const [city, setCity] = useState((d.city as string) || '');
  const [region, setRegion] = useState<string>((d.region as string) || '');
  const [specialty, setSpecialty] = useState<CandidateSpecialty | ''>((d.specialty as CandidateSpecialty) || '');
  const [workEnvironment, setWorkEnvironment] = useState<CandidateWorkEnvironment | ''>(
    (d.workEnvironment as CandidateWorkEnvironment) || ''
  );
  const [experience, setExperience] = useState<CandidateExperience | ''>((d.experience as CandidateExperience) || '');
  const [mobility, setMobility] = useState<CandidateMobility>((d.mobility as CandidateMobility) || 'non');
  const [mobilityRadiusKm, setMobilityRadiusKm] = useState<MobilityRadiusKm>((d.mobilityRadiusKm as MobilityRadiusKm) || 20);
  const [mobilityRegion, setMobilityRegion] = useState<string>((d.mobilityRegion as string) || '');
  const [mobilityDepartment, setMobilityDepartment] = useState<string>((d.mobilityDepartment as string) || '');
  const [housingNeed, setHousingNeed] = useState<CandidateHousingNeed>((d.housingNeed as CandidateHousingNeed) || 'non');
  const [phone, setPhone] = useState<string>((d.phone as string) || '');
  const [contactEmail, setContactEmail] = useState<string>((d.contactEmail as string) || email || '');
  const [isAvailableNow, setIsAvailableNow] = useState<boolean>(
    typeof d.isAvailableNow === 'boolean' ? (d.isAvailableNow as boolean) : true
  );
  const [availabilityMode, setAvailabilityMode] = useState<AvailabilityMode>(
    (d.availabilityMode as AvailabilityMode) || 'immediate'
  );
  const [periodStart, setPeriodStart] = useState<string>((d.periodStart as string) || '');
  const [periodEnd, setPeriodEnd] = useState<string>((d.periodEnd as string) || '');

  // Sauvegarde automatique du brouillon à chaque modification.
  useEffect(() => {
    saveDraft({
      qualification,
      city,
      region,
      specialty,
      workEnvironment,
      experience,
      mobility,
      mobilityRadiusKm,
      mobilityRegion,
      mobilityDepartment,
      housingNeed,
      phone,
      contactEmail,
      isAvailableNow,
      availabilityMode,
      periodStart,
      periodEnd,
    });
  }, [
    saveDraft,
    qualification,
    city,
    region,
    specialty,
    workEnvironment,
    experience,
    mobility,
    mobilityRadiusKm,
    mobilityRegion,
    mobilityDepartment,
    housingNeed,
    phone,
    contactEmail,
    isAvailableNow,
    availabilityMode,
    periodStart,
    periodEnd,
  ]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!city.trim()) {
      toast.error('Veuillez renseigner votre ville.');
      return;
    }
    if (!region.trim()) {
      toast.error('Veuillez sélectionner votre région.');
      return;
    }
    if (!phone.trim()) {
      toast.error('Veuillez renseigner votre téléphone.');
      return;
    }
    if (!contactEmail.trim() || !/^\S+@\S+\.\S+$/.test(contactEmail.trim())) {
      toast.error('Veuillez renseigner un email de contact valide.');
      return;
    }
    if (mobility === 'locale' && !mobilityRadiusKm) {
      toast.error('Veuillez préciser le rayon de mobilité (km).');
      return;
    }
    if (mobility === 'departementale' && !mobilityDepartment.trim()) {
      toast.error('Veuillez sélectionner votre département de mobilité.');
      return;
    }
    if (mobility === 'regionale' && !mobilityRegion.trim()) {
      toast.error('Veuillez sélectionner la région souhaitée.');
      return;
    }
    if (availabilityMode === 'period') {
      if (!periodStart || !periodEnd) {
        toast.error('Veuillez renseigner les dates de début et de fin de disponibilité.');
        return;
      }
      if (new Date(periodEnd).getTime() < new Date(periodStart).getTime()) {
        toast.error('La date de fin doit être postérieure à la date de début.');
        return;
      }
    }

    await onSave({
      qualification,
      city,
      region,
      // BNSSA : pas de spécialité ; MNS : pas de milieu (re-filtré côté hook upsert).
      specialty: qualification === 'BNSSA' ? undefined : specialty || undefined,
      workEnvironment: qualification === 'BNSSA' ? workEnvironment || undefined : undefined,
      experience: experience || undefined,
      mobility,
      mobilityRadiusKm: mobility === 'locale' ? mobilityRadiusKm : undefined,
      mobilityRegion: mobility === 'regionale' ? mobilityRegion : undefined,
      mobilityDepartment: mobility === 'departementale' ? mobilityDepartment : undefined,
      housingNeed,
      phone,
      email: contactEmail,
      isPublished: true,
      isAvailableNow: availabilityMode === 'immediate' ? isAvailableNow : false,
      availabilityMode,
      periodStart: availabilityMode === 'period' ? periodStart : undefined,
      periodEnd: availabilityMode === 'period' ? periodEnd : undefined,
      creatorEmail: contactEmail,
    });
    // Envoi réussi → on nettoie le brouillon local.
    clearDraft();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="relative bg-white w-full sm:max-w-2xl max-h-[92vh] sm:rounded-2xl rounded-t-2xl overflow-hidden flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-start gap-3 p-5 bg-gradient-to-r from-amber-50 via-white to-blue-50 border-b border-gray-100">
          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5 text-amber-600" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-bold text-gray-900">Je suis disponible !</h2>
            <p className="text-sm text-gray-600 mt-0.5">
              Renseignez votre profil pour apparaître <strong>anonymement</strong> dans la CVthèque
              « Ils sont disponibles ! ».
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="w-9 h-9 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 flex-shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-5 space-y-5 overflow-y-auto">
          {/* Mode de disponibilité */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-2">
              Type de disponibilité <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {[
                { value: 'immediate' as AvailabilityMode, title: 'Disponible immédiatement', desc: 'Visible 48h, renouvelable' },
                { value: 'period' as AvailabilityMode, title: 'Disponible sur une période', desc: 'Du / au — dates précises' },
              ].map((opt) => {
                const active = availabilityMode === opt.value;
                return (
                  <label
                    key={opt.value}
                    className={`flex items-start gap-2.5 px-3 py-2.5 rounded-xl border cursor-pointer text-sm transition-colors ${
                      active
                        ? 'border-blue-500 bg-blue-50 text-blue-900'
                        : 'border-gray-200 hover:border-gray-300 text-gray-700'
                    }`}
                  >
                    <input
                      type="radio"
                      name="availabilityMode"
                      value={opt.value}
                      checked={active}
                      onChange={() => setAvailabilityMode(opt.value)}
                      className="accent-blue-600 mt-0.5"
                    />
                    <span className="min-w-0">
                      <span className="block font-semibold">{opt.title}</span>
                      <span className="block text-[11px] text-gray-500">{opt.desc}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Période */}
          {availabilityMode === 'period' && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
              <label className="block text-xs font-semibold text-blue-900 mb-2">
                Période de disponibilité <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <span className="block text-[11px] font-semibold text-blue-900/80 mb-1">Du</span>
                  <input
                    type="date"
                    value={periodStart}
                    onChange={(e) => setPeriodStart(e.target.value)}
                    className="w-full px-3 py-2 border border-blue-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <span className="block text-[11px] font-semibold text-blue-900/80 mb-1">Au</span>
                  <input
                    type="date"
                    value={periodEnd}
                    min={periodStart || undefined}
                    onChange={(e) => setPeriodEnd(e.target.value)}
                    className="w-full px-3 py-2 border border-blue-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              {periodStart && periodEnd && (
                <p className="text-[11px] text-blue-900/80 mt-2">
                  Vous apparaîtrez comme disponible{' '}
                  <strong>{formatPeriodRange(periodStart, periodEnd)}</strong>.
                </p>
              )}
            </div>
          )}

          {/* Qualification */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-2">
              Vous êtes <span className="text-red-500">*</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {CANDIDATE_QUALIFICATIONS.map((q) => {
                const active = qualification === q;
                return (
                  <button
                    key={q}
                    type="button"
                    onClick={() => setQualification(q)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                      active
                        ? 'bg-gray-900 text-white border-gray-900'
                        : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {q}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Ville + Région */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Votre ville <span className="text-red-500">*</span>{' '}
                <span className="text-[10px] font-normal text-amber-700 uppercase tracking-wide">Privé</span>
              </label>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                required
                placeholder="Ex. Marseille, Lyon, Bordeaux…"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Votre région <span className="text-red-500">*</span>{' '}
                <span className="text-[10px] font-normal text-emerald-700 uppercase tracking-wide">Public</span>
              </label>
              <select
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">— Sélectionner une région —</option>
                {FRENCH_REGIONS.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Spécialité (MNS) OU Milieu (BNSSA) + Expérience (privés) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* BNSSA = surveillance uniquement → milieu ; MNS → spécialité. */}
            {qualification === 'BNSSA' ? (
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Milieu de travail{' '}
                  <span className="text-[10px] font-normal text-amber-700 uppercase tracking-wide">Privé</span>
                </label>
                <select
                  value={workEnvironment}
                  onChange={(e) => setWorkEnvironment(e.target.value as CandidateWorkEnvironment | '')}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">— Non précisé —</option>
                  {WORK_ENVIRONMENTS.map((w) => (
                    <option key={w} value={w}>{w}</option>
                  ))}
                </select>
              </div>
            ) : (
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Spécialité{' '}
                  <span className="text-[10px] font-normal text-amber-700 uppercase tracking-wide">Privé</span>
                </label>
                <select
                  value={specialty}
                  onChange={(e) => setSpecialty(e.target.value as CandidateSpecialty | '')}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">— Non précisée —</option>
                  {CANDIDATE_SPECIALTIES.map((s) => (
                    <option key={s} value={s}>{SPECIALTY_LABELS[s]}</option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Expérience{' '}
                <span className="text-[10px] font-normal text-amber-700 uppercase tracking-wide">Privé</span>
              </label>
              <select
                value={experience}
                onChange={(e) => setExperience(e.target.value as CandidateExperience | '')}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">— Non précisée —</option>
                {EXPERIENCE_LEVELS.map((x) => (
                  <option key={x} value={x}>{x}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Téléphone + Email */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Téléphone <span className="text-red-500">*</span>{' '}
                <span className="text-[10px] font-normal text-amber-700 uppercase tracking-wide">Privé</span>
              </label>
              <div className="relative">
                <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                  placeholder="06 12 34 56 78"
                  className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Email de contact <span className="text-red-500">*</span>{' '}
                <span className="text-[10px] font-normal text-amber-700 uppercase tracking-wide">Privé</span>
              </label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <input
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  required
                  placeholder="vous@exemple.com"
                  className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-xs text-blue-900">
            Aucun nom n'est publié : vous apparaissez sous un identifiant anonyme avec votre région,
            qualification, mobilité et besoin en logement. Vos coordonnées (téléphone, email) ne sont
            visibles que par les <strong>recruteurs connectés</strong>.
          </div>

          {/* Mobilité */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-2">
              Mobilité <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {[
                { value: 'non', label: 'Non' },
                { value: 'locale', label: 'Oui, localement' },
                { value: 'departementale', label: 'Oui, dans mon département' },
                { value: 'regionale', label: 'Oui, régionalement' },
                { value: 'france', label: 'Oui, partout en France' },
              ].map((opt) => {
                const active = mobility === opt.value;
                return (
                  <label
                    key={opt.value}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm transition-colors ${
                      active
                        ? 'border-blue-500 bg-blue-50 text-blue-800'
                        : 'border-gray-200 hover:border-gray-300 text-gray-700'
                    }`}
                  >
                    <input
                      type="radio"
                      name="mobility"
                      value={opt.value}
                      checked={active}
                      onChange={() => setMobility(opt.value as CandidateMobility)}
                      className="accent-blue-600"
                    />
                    {opt.label}
                  </label>
                );
              })}
            </div>

            {mobility === 'locale' && (
              <div className="mt-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
                <label className="block text-xs font-semibold text-blue-900 mb-2">
                  Dans un rayon de combien de km ? <span className="text-red-500">*</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {MOBILITY_RADIUS_OPTIONS.map((km) => {
                    const active = mobilityRadiusKm === km;
                    return (
                      <button
                        key={km}
                        type="button"
                        onClick={() => setMobilityRadiusKm(km)}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                          active
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-blue-800 border-blue-200 hover:border-blue-400'
                        }`}
                      >
                        {km} km
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {mobility === 'departementale' && (
              <div className="mt-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
                <label className="block text-xs font-semibold text-blue-900 mb-2">
                  Département de mobilité <span className="text-red-500">*</span>
                </label>
                <select
                  value={mobilityDepartment}
                  onChange={(e) => setMobilityDepartment(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-blue-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">— Sélectionner un département —</option>
                  {FRENCH_DEPARTMENTS.map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>
            )}

            {mobility === 'regionale' && (
              <div className="mt-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
                <label className="block text-xs font-semibold text-blue-900 mb-2">
                  Région souhaitée <span className="text-red-500">*</span>
                </label>
                <select
                  value={mobilityRegion}
                  onChange={(e) => setMobilityRegion(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-blue-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">— Sélectionner une région —</option>
                  {FRENCH_REGIONS.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Logement */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-2">
              Besoin d'un logement <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {[
                { value: 'non', label: 'Non' },
                { value: 'oui', label: 'Oui' },
                { value: 'oui_si_eloigne', label: 'Oui si mission éloignée' },
              ].map((opt) => {
                const active = housingNeed === opt.value;
                return (
                  <label
                    key={opt.value}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm transition-colors ${
                      active
                        ? 'border-amber-500 bg-amber-50 text-amber-800'
                        : 'border-gray-200 hover:border-gray-300 text-gray-700'
                    }`}
                  >
                    <input
                      type="radio"
                      name="housingNeed"
                      value={opt.value}
                      checked={active}
                      onChange={() => setHousingNeed(opt.value as CandidateHousingNeed)}
                      className="accent-amber-600"
                    />
                    {opt.label}
                  </label>
                );
              })}
            </div>
          </div>

          {/* Disponible immédiatement (mode immédiat) */}
          {availabilityMode === 'immediate' && (
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-2">
                Disponible immédiatement&nbsp;? <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-2 max-w-md">
                {[
                  { value: true, label: 'Oui' },
                  { value: false, label: 'Non' },
                ].map((opt) => {
                  const active = isAvailableNow === opt.value;
                  return (
                    <label
                      key={String(opt.value)}
                      className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm font-semibold transition-colors ${
                        active
                          ? opt.value
                            ? 'border-emerald-500 bg-emerald-50 text-emerald-800'
                            : 'border-gray-400 bg-gray-100 text-gray-700'
                          : 'border-gray-200 hover:border-gray-300 text-gray-700'
                      }`}
                    >
                      <input
                        type="radio"
                        name="isAvailableNow"
                        checked={active}
                        onChange={() => setIsAvailableNow(opt.value)}
                        className={opt.value ? 'accent-emerald-600' : 'accent-gray-500'}
                      />
                      {opt.label}
                    </label>
                  );
                })}
              </div>
              <p className="text-[11px] text-gray-400 mt-1">
                Si « Oui », votre profil reste visible <strong>48 heures</strong> dans la CVthèque.
              </p>
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="p-4 border-t border-gray-100 bg-white flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-100"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white px-5 py-2.5 rounded-xl text-sm font-bold disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {submitLabel}
            {!saving && <ArrowRight className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}
